import { type PreconfiguredResponse } from '@rsos/reduxDataStore/src/features/preconfiguredMessages/preconfiguredMessagesAPI';
import {
  PreparedResponseType,
  SINGLETON_RESPONSE_TYPES,
} from '@rsos/reduxDataStore/src/features/preconfiguredMessages/preconfiguredMessagesTypes';
import {
  isAuthorityMessage,
  isSystemMessage,
} from '@rsos/reduxDataStore/src/features/preconfiguredMessages/preconfiguredMessagesUtils';

// Smart-phrase detection and interpolation helpers now live in the shared
// reduxDataStore feature so both this app and the admin app consume one source.
// Re-exported here to preserve existing import paths within emergency_data.
export {
  hasMatchingSmartPhrases,
  hasDisabledSmartPhrases,
  messageRequiresLocation,
  messageRequiresRoadside,
  messageRequiresCallerId,
  handleDynamicMessage,
  type ResolvedPreconfiguredResponse,
} from '@rsos/reduxDataStore/src/features/preconfiguredMessages/preconfiguredMessagesUtils';

/**
 * Narrows the preconfigured messages down to those that should appear in the
 * All Messages panel:
 * - Ending messages are excluded — the End Chat action owns that flow, so they
 *   should not show up as sendable messages here.
 * - For each singleton assigned type (e.g. location_request), the rapidsos
 *   system default is hidden when the authority has configured its own message
 *   of that type, so only the active override is listed rather than both it and
 *   the now-redundant default.
 */
export const getDisplayableMessages = (
  preconfiguredMessages: PreconfiguredResponse[],
): PreconfiguredResponse[] => {
  // Singleton slots the authority has overridden with their own message; the
  // rapidsos system default for these is redundant and hidden below. Restricted
  // to singleton types on purpose — an authority message may instead be `custom`
  // or (for older/untyped rows) `null`, and those must not hide anything.
  const overriddenTypes = new Set(
    preconfiguredMessages
      .filter(
        message =>
          isAuthorityMessage(message) &&
          message.type != null &&
          SINGLETON_RESPONSE_TYPES.includes(message.type),
      )
      .map(message => message.type),
  );

  return preconfiguredMessages.filter(message => {
    // The End Chat action owns ending messages — never list them here.
    if (message.type === PreparedResponseType.ending) {
      return false;
    }

    // Hide a system default whose singleton slot the authority has overridden.
    if (isSystemMessage(message) && overriddenTypes.has(message.type)) {
      return false;
    }

    return true;
  });
};

// Whether a "Request Location" message (which backs the Request Location
// button) is configured. Detected by the location_request type so it matches
// both the rapidsos system default and any authority-configured custom message.
export const hasRequestLocationMessage = (
  messages: PreconfiguredResponse[] | undefined,
): boolean =>
  messages?.some(
    message => message.type === PreparedResponseType.locationRequest,
  ) ?? false;

export const getMatchingPreconfigured = (
  preconfiguredMessages: PreconfiguredResponse[],
  searchedMessage: string,
) => {
  return preconfiguredMessages.filter(preconfiguredMessage => {
    const lowerSearchedMessage = searchedMessage.toLowerCase();

    const isMatchingTitle = preconfiguredMessage.title
      .toLowerCase()
      .includes(lowerSearchedMessage);

    const isMatchingMessage = preconfiguredMessage.body
      .toLowerCase()
      .includes(lowerSearchedMessage);

    return isMatchingMessage || isMatchingTitle;
  });
};
